/**
 * Binds a session (CMS, customer portal or TSP portal) to this browser so a
 * copied access token is useless.
 *
 * At login we create an ECDSA P-256 key pair whose private key can never be
 * exported (not even by our own code) and keep it in IndexedDB. The server
 * puts the public key inside the access token. Every API call then carries a
 * fresh signature over:
 *
 *   METHOD \n /api/path \n timestamp(ms) \n nonce \n sha256(access_token)
 *
 * The server rejects a bound token whose request is not signed by that key,
 * and rejects these sessions outright when they are not bound.
 * Must match api/assets/requestSignature.js on the backend.
 *
 * Each portal has its own key ("scope"), so signing in to the customer or TSP
 * portal in the same browser does not replace the CMS key.
 */

const DB_NAME = "mi-auth";
const STORE_NAME = "keys";
const KEY_IDS = { cms: "session-key", customer: "customer-session-key", tsp: "tsp-session-key" };
const keyIdFor = (scope) => KEY_IDS[scope] || KEY_IDS.cms;
const CLOCK_OFFSET_KEY = "mi_server_clock_offset";

const encoder = new TextEncoder();

export const isSigningSupported = () =>
	typeof window !== "undefined" &&
	!!window.isSecureContext &&
	!!(window.crypto && window.crypto.subtle) &&
	!!window.indexedDB;

/* ---------- IndexedDB (non-extractable keys can be stored, not exported) ---------- */

const openDb = () =>
	new Promise((resolve, reject) => {
		const request = window.indexedDB.open(DB_NAME, 1);
		request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});

const withStore = (mode, action) =>
	openDb().then(
		(db) =>
			new Promise((resolve, reject) => {
				const tx = db.transaction(STORE_NAME, mode);
				const request = action(tx.objectStore(STORE_NAME));
				tx.oncomplete = () => {
					db.close();
					resolve(request ? request.result : undefined);
				};
				tx.onerror = tx.onabort = () => {
					db.close();
					reject(tx.error);
				};
			})
	);

// Read from IndexedDB every time (cheap) so all tabs use the key of the
// current session, even after a login in another tab.
const loadKeyPair = async (scope) => {
	if (!isSigningSupported()) return null;
	try {
		return (await withStore("readonly", (store) => store.get(keyIdFor(scope)))) || null;
	} catch (e) {
		return null;
	}
};

/**
 * Create a fresh key for a new login. Returns the public key (JWK JSON string)
 * to send as `public_key`, or null when the browser cannot sign (the server
 * then refuses the login with a message asking for an up-to-date browser).
 */
export const createLoginKey = async (scope = "cms") => {
	if (!isSigningSupported()) return null;
	try {
		const pair = await window.crypto.subtle.generateKey(
			{ name: "ECDSA", namedCurve: "P-256" },
			false, // private key can never be exported
			["sign", "verify"]
		);
		const jwk = await window.crypto.subtle.exportKey("jwk", pair.publicKey);
		await withStore("readwrite", (store) =>
			store.put({ publicKey: pair.publicKey, privateKey: pair.privateKey }, keyIdFor(scope))
		);
		return JSON.stringify({ kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y });
	} catch (e) {
		console.warn("Request signing unavailable:", e);
		return null;
	}
};

export const clearLoginKey = async (scope = "cms") => {
	if (!isSigningSupported()) return;
	try {
		await withStore("readwrite", (store) => store.delete(keyIdFor(scope)));
	} catch (e) {
		// nothing to clear
	}
};

/* ---------- browser id (one session per portal per browser) ---------- */

const BROWSER_ID_KEY = "mi_browser_id";

/**
 * Random id of this browser, sent as `browser_id` at login. The server ends
 * this browser's previous session on the same portal when a new one starts.
 */
export const getBrowserId = () => {
	try {
		let id = localStorage.getItem(BROWSER_ID_KEY);
		if (!id) {
			const bytes = new Uint8Array(16);
			window.crypto.getRandomValues(bytes);
			id = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
			localStorage.setItem(BROWSER_ID_KEY, id);
		}
		return id;
	} catch (e) {
		return undefined;
	}
};

/* ---------- server clock (signatures must be within 5 minutes) ---------- */

const readClockOffset = () => {
	try {
		return Number(localStorage.getItem(CLOCK_OFFSET_KEY)) || 0;
	} catch (e) {
		return 0;
	}
};

export const updateServerTime = (serverTime) => {
	const time = Number(serverTime);
	if (!time) return;
	try {
		localStorage.setItem(CLOCK_OFFSET_KEY, String(time - Date.now()));
	} catch (e) {
		// ignore
	}
};

/* ---------- signing ---------- */

const toHex = (buffer) =>
	Array.from(new Uint8Array(buffer))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");

const toBase64 = (buffer) => {
	let binary = "";
	new Uint8Array(buffer).forEach((b) => {
		binary += String.fromCharCode(b);
	});
	return window.btoa(binary);
};

const randomNonce = () => {
	const bytes = new Uint8Array(16);
	window.crypto.getRandomValues(bytes);
	return toHex(bytes);
};

// Same rule as the server: the path from "/api/", without the query string.
const signedPath = (url) => {
	const path = new URL(url, window.location.origin).pathname;
	const apiIndex = path.indexOf("/api/");
	return apiIndex >= 0 ? path.slice(apiIndex) : path;
};

const sign = async (method, path, token, scope) => {
	const pair = await loadKeyPair(scope);
	if (!pair || !token) return null;
	const timestamp = String(Date.now() + readClockOffset());
	const nonce = randomNonce();
	const tokenHash = toHex(await window.crypto.subtle.digest("SHA-256", encoder.encode(token)));
	const payload = [method.toUpperCase(), path, timestamp, nonce, tokenHash].join("\n");
	const signature = await window.crypto.subtle.sign(
		{ name: "ECDSA", hash: "SHA-256" },
		pair.privateKey,
		encoder.encode(payload)
	);
	return { timestamp, nonce, signature: toBase64(signature) };
};

/** Headers to add to an API request made with `token` of the given portal scope. */
export const getSignatureHeaders = async (method, url, token, scope = "cms") => {
	try {
		const proof = await sign(method, signedPath(url), token, scope);
		if (!proof) return {};
		return {
			"x-mi-signature": proof.signature,
			"x-mi-timestamp": proof.timestamp,
			"x-mi-nonce": proof.nonce,
		};
	} catch (e) {
		console.warn("Could not sign request:", e);
		return {};
	}
};

/** Socket.IO handshake auth for `token`. */
export const getSocketAuth = async (token) => {
	try {
		const proof = await sign("SOCKET", "/socket.io", token, "cms");
		return proof ? { token, ...proof } : { token };
	} catch (e) {
		return { token };
	}
};

/* ---------- 7-day session expiry ---------- */

/** Expiry time (ms) from the token's `exp` claim, or null if it has none. */
export const getTokenExpiry = (token) => {
	try {
		const part = String(token).split(".")[1];
		const json = window.atob(part.replace(/-/g, "+").replace(/_/g, "/"));
		const { exp } = JSON.parse(json);
		return exp ? exp * 1000 : null;
	} catch (e) {
		return null;
	}
};

export const isTokenExpired = (token) => {
	const expiry = getTokenExpiry(token);
	return !!expiry && Date.now() + readClockOffset() >= expiry;
};
